'use client';

import { Skeleton } from '@common/components';
import { FOCUS_RING } from '@common/components/interaction';
import { cn } from '@core/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { UnauthQueryOptions } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { PointCoin } from '@/app/_components/PointCoin';
import { Image } from '@/components/client';
import { ROUTES, WEL_MARKET_ROUTES } from '@/constants';
import { formatPrice } from '@/utils/formatPrice';

import { SPECIALTY_PRODUCT_QUERY } from '../_constants';
import { BenefitHomeSectionHeader } from './BenefitHomeSectionHeader';
import { BENEFIT_SECTION_CLASS_NAME } from './BenefitMenuSection';

const FALLBACK_CARD_COUNT = 4;

const CAROUSEL_CLASS_NAME =
  '-mx-20 flex gap-12 overflow-x-auto px-20 py-4 hide-scrollbar webkit-scroll-touch';

const CAROUSEL_ITEM_CLASS_NAME = 'flex w-104 shrink-0';

const useSpecialtyProducts = () =>
  useSuspenseQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/local-market/product-feeds'](
      SPECIALTY_PRODUCT_QUERY,
    ),
    select: (response) => response.context?.items ?? [],
  });

type SpecialtyProduct = ReturnType<typeof useSpecialtyProducts>['data'][number];

const SectionHeading = () => (
  <div className="flex flex-col gap-4">
    <BenefitHomeSectionHeader
      icon={<PointCoin currency="WELLO_POINT" size={24} />}
      title="특산품"
      moreHref={ROUTES.SEARCH.withSearchParams({
        searchParams: { category: 'welmarket' },
      })}
    />
    <p className="font-500 text-Gray-550 typo-body4">
      <span className="text-Y-600">웰포인트</span>로 만나는 특산품
    </p>
  </div>
);

const SpecialtyProductCard = ({ product }: { product: SpecialtyProduct }) => {
  const {
    productId,
    slugId,
    productName,
    mainImageUrl,
    price,
    salePhase,
    soldOut,
    gov,
  } = product;

  const isSoldOut = soldOut && salePhase === 'ON_SALE';
  const isNotOnSale =
    (salePhase !== 'ON_SALE' && salePhase !== undefined) || isSoldOut;
  const salePhaseLabel = isSoldOut
    ? '품절'
    : salePhase === 'SALE_ENDED'
      ? '판매 종료'
      : '상품 준비중이에요';

  const regularPrice = price?.regularPrice ?? 0;
  const expectedFinalPrice = price?.expectedFinalPrice ?? regularPrice;
  const discountRate = price?.totalDiscountRate ?? 0;
  const hasDiscount = expectedFinalPrice !== regularPrice;

  return (
    <CustomLink
      href={WEL_MARKET_ROUTES.PRODUCT_DETAIL.pathname({
        productNumber: slugId ?? productId ?? '',
      })}
      className={cn(
        'group flex min-w-0 flex-1 flex-col gap-8 rounded-large',
        FOCUS_RING,
      )}
    >
      <span className="relative aspect-square w-full overflow-hidden rounded-large border border-Gray-black/5 bg-Gray-100 transition-opacity duration-150 group-hover:opacity-80 group-active:opacity-80 motion-reduce:transition-none">
        {mainImageUrl ? (
          <Image
            src={mainImageUrl}
            alt=""
            fill
            sizes="108px"
            className="object-cover"
          />
        ) : null}
        {isNotOnSale ? (
          <span className="absolute inset-0 flex items-center justify-center bg-Gray-black/40 font-700 text-Gray-white typo-caption1">
            {salePhaseLabel}
          </span>
        ) : null}
      </span>
      <span className="flex flex-col gap-2">
        <span className="truncate font-600 text-Gray-600 typo-caption2">
          {gov?.govName}
        </span>
        <span className="line-clamp-2 font-600 text-Gray-black typo-caption1">
          {productName}
        </span>
        {hasDiscount ? (
          <span className="font-400 text-Gray-500 line-through typo-caption1">
            {formatPrice(regularPrice)}
          </span>
        ) : null}
        <span className="flex items-baseline gap-4 text-Gray-black">
          {hasDiscount && discountRate > 0 ? (
            <span className="font-500 text-O-500 typo-body4">
              {discountRate}%
            </span>
          ) : null}
          <span>
            <span className="font-700 typo-body4">
              {expectedFinalPrice.toLocaleString()}
            </span>
            <span className="font-600 typo-caption1">원</span>
          </span>
        </span>
      </span>
    </CustomLink>
  );
};

const SpecialtyProductCarousel = () => {
  const { data: products } = useSpecialtyProducts();

  if (products.length === 0) {
    return (
      <section className={BENEFIT_SECTION_CLASS_NAME}>
        <SectionHeading />
        <p className="flex min-h-120 items-center justify-center rounded-medium bg-Gray-50 font-500 text-Gray-500 typo-body4">
          상품을 준비하고 있어요.
        </p>
      </section>
    );
  }

  return (
    <section className={BENEFIT_SECTION_CLASS_NAME}>
      <SectionHeading />
      <ul className={CAROUSEL_CLASS_NAME}>
        {products.map((product, index) => (
          <li
            key={product.productId ?? product.slugId ?? index}
            className={CAROUSEL_ITEM_CLASS_NAME}
          >
            <SpecialtyProductCard product={product} />
          </li>
        ))}
      </ul>
    </section>
  );
};

const SpecialtyProductCarouselFallback = () => (
  <section className={BENEFIT_SECTION_CLASS_NAME}>
    <SectionHeading />
    <ul aria-hidden className={CAROUSEL_CLASS_NAME}>
      {Array.from({ length: FALLBACK_CARD_COUNT }, (_, index) => (
        <li key={index} className={CAROUSEL_ITEM_CLASS_NAME}>
          <span className="flex flex-1 flex-col gap-8">
            <Skeleton className="aspect-square w-full rounded-large" />
            <Skeleton typo="caption1" className="w-full rounded-xxsmall" />
            <Skeleton typo="body4" className="w-2/3 rounded-xxsmall" />
          </span>
        </li>
      ))}
    </ul>
  </section>
);

export const BenefitSpecialtySection = () => (
  <ErrorBoundary fallback={null}>
    <Suspense fallback={<SpecialtyProductCarouselFallback />}>
      <SpecialtyProductCarousel />
    </Suspense>
  </ErrorBoundary>
);
